import { StrictMode, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import {
  Archive,
  AtSign,
  BadgeCheck,
  CalendarClock,
  Check,
  CheckCheck,
  CircleCheckBig,
  CircleX,
  Clipboard,
  Clock3,
  ExternalLink,
  FileText,
  Filter,
  Globe2,
  History,
  Heart,
  Image as ImageIcon,
  Languages,
  LayoutList,
  Link2,
  LoaderCircle,
  Lock,
  LogOut,
  Megaphone,
  MessageCircle,
  Pencil,
  Plus,
  RadioTower,
  Rocket,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  ThumbsUp,
  TimerReset,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import "./styles.css";

type PostStatus = "draft" | "pending" | "approved" | "rejected" | "published" | "expired";
type ContentType = "persistent" | "ephemeral";
type ChannelMode = "api" | "link" | "manual";

type Asset = {
  role: string;
  url: string;
  alt?: string;
  format?: string;
  source_path?: string;
  safety?: "sfw" | "mature" | "explicit";
  duration_seconds?: number;
};
type ChannelAction = {
  id: string;
  post_id: string;
  channel: string;
  label: string;
  mode: ChannelMode;
  status: string;
  target_url: string;
  adapter: string;
  locale: string;
  asset_role: string;
  copy_text: string;
  scheduled_at: number | null;
  published_at: number | null;
  external_url: string;
  last_error: string;
};
type CommunicationPost = {
  id: string;
  title: string;
  summary: string;
  game_slug: string;
  campaign: string;
  content_type: ContentType;
  status: PostStatus;
  priority: number;
  audience: string;
  publish_after: number | null;
  expires_at: number | null;
  locales: Record<string, string>;
  assets: Asset[];
  channels: ChannelAction[];
  source: string;
  created_at: number;
  updated_at: number;
  approved_at: number | null;
  published_at: number | null;
  rejection_note: string;
};
type AuditEntry = {
  id: string;
  post_id: string | null;
  action_id: string | null;
  action: string;
  actor: string;
  detail_json: string;
  created_at: number;
};
type SocialNetwork = {
  id: string;
  label: string;
  handle: string;
  profile_url: string;
  tier: "core" | "discovery" | "experiment";
  audience: string;
  growth_focus: string;
  cadence: string;
  constraints: string;
  sort_order: number;
  is_active: number;
  researched_at: number;
};
type SocialActionStatus = "proposed" | "approved" | "declined" | "completed";
type SocialAction = {
  id: string;
  network_id: string;
  kind: "reply" | "comment" | "follow" | "like" | "upvote" | "boost" | "react" | "community_care" | "profile" | "research";
  status: SocialActionStatus;
  priority: number;
  title: string;
  target_name: string;
  target_url: string;
  target_excerpt: string;
  draft_text: string;
  rationale: string;
  guardrails: string;
  source: string;
  expires_at: number | null;
  created_at: number;
  updated_at: number;
  approved_at: number | null;
  declined_at: number | null;
  completed_at: number | null;
  execution_state: "pending" | "blocked" | "completed" | "canceled";
  execution_error: string;
  external_url: string;
  attempted_at: number | null;
};
type PlatformHealth = {
  id: string;
  label: string;
  target_hours: number;
  format: string;
  publisher: "api" | "manual" | "blocked";
  configured: boolean;
  last_published_at: number | null;
  next_due_at: number;
  overdue: boolean;
  queued: number;
  failed: number;
  state: "healthy" | "due" | "blocked";
};
type AppState = {
  posts: CommunicationPost[];
  audit: AuditEntry[];
  stats: Record<string, number>;
  social_networks: SocialNetwork[];
  social_actions: SocialAction[];
  platform_health: PlatformHealth[];
  server_time: number;
};

type View = "review" | "approved" | "published" | "archive" | "rotation" | "social" | "activity";

const localeLabels: Record<string, string> = {
  en: "English",
  fr: "Français",
  de: "Deutsch",
  es: "Español",
  it: "Italiano",
  pt: "Português",
  ru: "Русский",
  zh: "中文",
  ja: "日本語",
  ko: "한국어",
};

const assetRoleLabels: Record<string, string> = {
  persistent_hero: "Persistent hero",
  persistent_square: "Persistent square",
  persistent_detail: "Persistent detail",
  ephemeral_story: "Ephemeral story",
  ephemeral_reel: "Ephemeral reel",
  ephemeral_square: "Ephemeral square",
  safe_social: "Public safe",
  thumbnail: "Thumbnail",
  trailer_sfw_short: "SFW trailer · short",
  trailer_sfw_short_vertical: "SFW trailer · short vertical",
  trailer_sfw_long: "SFW trailer · long",
  trailer_nsfw_short: "NSFW trailer · short",
  trailer_nsfw_long: "NSFW trailer · long",
};

const channelDirectory = [
  { id: "website-news", label: "Shastes news", mode: "manual", url: "https://shastes.com/news/" },
  { id: "patreon", label: "Patreon", mode: "link", url: "https://www.patreon.com/posts/new" },
  { id: "discord", label: "Discord", mode: "api", adapter: "discord", url: "https://discord.com/channels/@me" },
  { id: "bluesky", label: "Bluesky", mode: "link", url: "https://bsky.app/" },
  { id: "mastodon", label: "Mastodon", mode: "link", adapter: "manual", url: "https://mastodon.social/publish" },
  { id: "x", label: "X", mode: "link", url: "https://x.com/compose/post" },
  { id: "instagram", label: "Instagram", mode: "link", url: "https://www.instagram.com/" },
  { id: "tiktok", label: "TikTok", mode: "link", url: "https://www.tiktok.com/upload" },
  { id: "reddit", label: "Reddit", mode: "link", url: "https://www.reddit.com/submit" },
  { id: "tumblr", label: "Tumblr", mode: "link", url: "https://www.tumblr.com/new/text" },
  { id: "itch", label: "itch.io", mode: "link", url: "https://itch.io/dashboard" },
  { id: "youtube", label: "YouTube", mode: "link", url: "https://studio.youtube.com/" },
  { id: "game-jolt", label: "Game Jolt", mode: "link", url: "https://gamejolt.com/" },
  { id: "newgrounds", label: "Newgrounds", mode: "link", url: "https://www.newgrounds.com/account" },
  { id: "pixiv", label: "Pixiv", mode: "link", url: "https://www.pixiv.net/" },
  { id: "deviantart", label: "DeviantArt", mode: "link", url: "https://www.deviantart.com/submit/" },
  { id: "vndb", label: "VNDB", mode: "link", url: "https://vndb.org/" },
  { id: "fenoxo-forums", label: "Fenoxo Forums", mode: "link", url: "https://forum.fenoxo.com/" },
  { id: "horni-io", label: "Horni.io", mode: "link", url: "https://horni.io/" },
  { id: "newsletter", label: "Newsletter", mode: "manual", url: "" },
] as const;

const defaultChannelIds = ["website-news", "patreon", "discord", "bluesky", "mastodon", "x", "instagram", "reddit"];

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      "x-shaste-request": "approval-hub",
      ...init.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || `Request failed (${response.status}).`);
  return payload;
}

function formatDate(seconds: number | null, includeTime = true): string {
  if (!seconds) return "Not set";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(new Date(seconds * 1000));
}

function inputDate(seconds: number | null): string {
  if (!seconds) return "";
  const date = new Date(seconds * 1000);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function parseInputDate(value: string): number | null {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? Math.round(timestamp / 1000) : null;
}

function remainingLabel(seconds: number | null, now: number): string {
  if (!seconds) return "No deadline";
  const delta = seconds - now;
  if (delta <= 0) return "Expired";
  const hours = Math.floor(delta / 3600);
  if (hours < 24) return `${Math.max(1, hours)}h left`;
  return `${Math.ceil(hours / 24)}d left`;
}

function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [state, setState] = useState<AppState>({ posts: [], audit: [], stats: {}, social_networks: [], social_actions: [], platform_health: [], server_time: Math.floor(Date.now() / 1000) });
  const [activeView, setActiveView] = useState<View>("review");
  const [activePostId, setActivePostId] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | ContentType>("all");
  const [notice, setNotice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [openedActionId, setOpenedActionId] = useState("");
  const [activeNetworkId, setActiveNetworkId] = useState("");
  const [socialFilter, setSocialFilter] = useState<"review" | "approved" | "declined" | "all">("review");

  const load = async () => {
    const payload = await api<AppState & { ok: boolean }>("/api/state");
    setState(payload);
    setAuthenticated(true);
  };

  useEffect(() => {
    api<{ authenticated: boolean }>("/api/me")
      .then(() => load())
      .catch(() => setAuthenticated(false));
  }, []);

  useEffect(() => {
    if (!activeNetworkId && state.social_networks[0]) setActiveNetworkId(state.social_networks[0].id);
  }, [activeNetworkId, state.social_networks]);

  const visiblePosts = useMemo(() => {
    const query = search.trim().toLowerCase();
    const statusMatches = (post: CommunicationPost) => {
      if (activeView === "review") return ["draft", "pending", "rejected"].includes(post.status);
      if (activeView === "approved") return post.status === "approved";
      if (activeView === "published") return post.status === "published";
      if (activeView === "archive") return post.status === "expired";
      return false;
    };
    return state.posts.filter((post) => {
      if (!statusMatches(post)) return false;
      if (typeFilter !== "all" && post.content_type !== typeFilter) return false;
      if (!query) return true;
      return [post.title, post.summary, post.game_slug, post.campaign, ...post.channels.map((channel) => channel.label)]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [activeView, search, state.posts, typeFilter]);

  const activePost = visiblePosts.find((post) => post.id === activePostId) || visiblePosts[0] || null;
  useEffect(() => {
    if (activePost && activePost.id !== activePostId) setActivePostId(activePost.id);
  }, [activePost, activePostId]);

  const run = async (label: string, operation: () => Promise<void>) => {
    setBusy(label);
    setErrorMessage("");
    setNotice("");
    try {
      await operation();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong.");
    } finally {
      setBusy("");
    }
  };

  const login = (password: string) =>
    run("Unlocking", async () => {
      await api("/api/session", { method: "POST", body: JSON.stringify({ password }) });
      await load();
    });

  const logout = () =>
    run("Signing out", async () => {
      await api("/api/session", { method: "DELETE" });
      setAuthenticated(false);
    });

  const approve = (ids: string[]) =>
    run("Approving", async () => {
      await api("/api/posts/bulk/approve", { method: "POST", body: JSON.stringify({ ids }) });
      setSelectedIds([]);
      setNotice(ids.length > 1 ? `${ids.length} posts approved.` : "Post approved.");
      await load();
    });

  const reject = (post: CommunicationPost, note: string) =>
    run("Sending back", async () => {
      await api(`/api/posts/${post.id}/reject`, { method: "POST", body: JSON.stringify({ note }) });
      setNotice("Post returned for changes.");
      await load();
    });

  const save = (post: CommunicationPost) =>
    run("Saving", async () => {
      await api(`/api/posts/${post.id}`, { method: "PUT", body: JSON.stringify(post) });
      setNotice("Changes saved.");
      await load();
    });

  const publishAction = (post: CommunicationPost, action: ChannelAction) =>
    run(`Preparing ${action.label}`, async () => {
      const result = await api<{ mode: string; open_url?: string; copy_text?: string; external_url?: string }>(
        `/api/posts/${post.id}/channels/${action.id}/publish`,
        { method: "POST" },
      );
      if (result.copy_text) await navigator.clipboard.writeText(result.copy_text);
      if (result.open_url) window.open(result.open_url, "_blank", "noopener,noreferrer");
      setOpenedActionId(result.mode === "api" ? "" : action.id);
      setNotice(result.mode === "api" ? `${action.label} published.` : `Copy ready and ${action.label} opened.`);
      await load();
    });

  const markPublished = (post: CommunicationPost, action: ChannelAction) =>
    run(`Completing ${action.label}`, async () => {
      await api(`/api/posts/${post.id}/channels/${action.id}/mark-published`, { method: "POST", body: "{}" });
      setOpenedActionId("");
      setNotice(`${action.label} marked as published.`);
      await load();
    });

  const createPost = (payload: unknown) =>
    run("Creating", async () => {
      const result = await api<{ id: string }>("/api/posts", { method: "POST", body: JSON.stringify(payload) });
      setCreateOpen(false);
      setActiveView("review");
      setActivePostId(result.id);
      setNotice("Communication added to review.");
      await load();
    });

  const saveSocialAction = (action: SocialAction) =>
    run("Saving proposal", async () => {
      await api(`/api/social-actions/${action.id}`, { method: "PUT", body: JSON.stringify(action) });
      setNotice("Proposal updated. Nothing was sent to the network.");
      await load();
    });

  const transitionSocialAction = (action: SocialAction, next: "approve" | "decline" | "complete") =>
    run(next === "approve" ? "Approving proposal" : next === "decline" ? "Declining proposal" : "Recording completion", async () => {
      await api(`/api/social-actions/${action.id}/${next}`, { method: "POST", body: "{}" });
      setNotice(next === "approve"
        ? "Action approved. The hourly automation may now perform this exact action."
        : next === "decline"
          ? "Proposal declined."
          : "Manual action recorded as complete.");
      await load();
    });

  if (authenticated === null) return <BootScreen />;
  if (!authenticated) return <LoginScreen loading={Boolean(busy)} error={errorMessage} onLogin={login} />;

  const pendingCount = state.posts.filter((post) => ["draft", "pending", "rejected"].includes(post.status)).length;
  const proposedSocialCount = state.social_actions.filter((action) => action.status === "proposed").length;
  const reviewMinutes = Math.max(1, Math.ceil(pendingCount * 0.75));

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">S</span>
          <div>
            <strong>Shaste Comms</strong>
            <small>Approval desk</small>
          </div>
        </div>

        <nav className="nav-list" aria-label="Communication views">
          <NavButton icon={<LayoutList size={17} />} label="Review" count={pendingCount} selected={activeView === "review"} onClick={() => setActiveView("review")} />
          <NavButton icon={<ShieldCheck size={17} />} label="Approved" count={Number(state.stats.approved || 0)} selected={activeView === "approved"} onClick={() => setActiveView("approved")} />
          <NavButton icon={<Rocket size={17} />} label="Published" count={Number(state.stats.published || 0)} selected={activeView === "published"} onClick={() => setActiveView("published")} />
          <NavButton icon={<Archive size={17} />} label="Archive" count={Number(state.stats.expired || 0)} selected={activeView === "archive"} onClick={() => setActiveView("archive")} />
          <NavButton icon={<RadioTower size={17} />} label="Rotation" count={state.platform_health.filter((platform) => platform.overdue).length} selected={activeView === "rotation"} onClick={() => setActiveView("rotation")} />
          <NavButton icon={<Users size={17} />} label="Social growth" count={proposedSocialCount} selected={activeView === "social"} onClick={() => setActiveView("social")} />
          <NavButton icon={<History size={17} />} label="Activity" selected={activeView === "activity"} onClick={() => setActiveView("activity")} />
        </nav>

        <div className="strategy-block">
          <p>Focus</p>
          <strong>Vorenka worlds</strong>
          <span>Long-term depth</span>
          <strong>Realistic games</strong>
          <span>Fast acquisition</span>
        </div>

        <button className="sidebar-button" onClick={logout} type="button">
          <LogOut size={16} /> Sign out
        </button>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <p className="eyebrow">Communication operations</p>
            <h1>{viewTitle(activeView)}</h1>
          </div>
          <div className="topbar-actions">
            {activeView === "social" ? <span className="review-time"><ShieldCheck size={15} /> Approved actions checked hourly</span> : <span className="review-time"><Clock3 size={15} /> About {reviewMinutes} min</span>}
            {!["rotation", "social", "activity"].includes(activeView) ? <button className="primary-button" onClick={() => setCreateOpen(true)} type="button"><Plus size={17} /> New post</button> : null}
          </div>
        </header>

        {errorMessage ? <Banner tone="error" icon={<X size={16} />} text={errorMessage} /> : null}
        {notice ? <Banner tone="success" icon={<Check size={16} />} text={notice} /> : null}
        {busy ? <Banner tone="busy" icon={<LoaderCircle className="spin" size={16} />} text={busy} /> : null}

        {activeView === "activity" ? (
          <ActivityView audit={state.audit} posts={state.posts} />
        ) : activeView === "rotation" ? (
          <RotationView platforms={state.platform_health} now={state.server_time} />
        ) : activeView === "social" ? (
          <SocialGrowthView
            networks={state.social_networks}
            actions={state.social_actions}
            activeNetworkId={activeNetworkId}
            filter={socialFilter}
            busy={Boolean(busy)}
            onSelectNetwork={setActiveNetworkId}
            onFilter={setSocialFilter}
            onSave={saveSocialAction}
            onApprove={(action) => transitionSocialAction(action, "approve")}
            onDecline={(action) => transitionSocialAction(action, "decline")}
            onComplete={(action) => transitionSocialAction(action, "complete")}
          />
        ) : (
          <>
            <section className="metrics" aria-label="Communication metrics">
              <Metric icon={<LayoutList size={18} />} label="To review" value={pendingCount} tone="amber" />
              <Metric icon={<FileText size={18} />} label="Persistent" value={Number(state.stats.persistent_active || 0)} tone="blue" />
              <Metric icon={<TimerReset size={18} />} label="Ephemeral" value={Number(state.stats.ephemeral_active || 0)} tone="coral" />
              <Metric icon={<CheckCheck size={18} />} label="Approved" value={Number(state.stats.approved || 0)} tone="green" />
            </section>

            <section className="toolbar">
              <label className="search-field"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search campaigns, games or channels" /></label>
              <div className="segmented" aria-label="Content type">
                <button className={typeFilter === "all" ? "selected" : ""} onClick={() => setTypeFilter("all")} type="button">All</button>
                <button className={typeFilter === "persistent" ? "selected" : ""} onClick={() => setTypeFilter("persistent")} type="button">Persistent</button>
                <button className={typeFilter === "ephemeral" ? "selected" : ""} onClick={() => setTypeFilter("ephemeral")} type="button">Ephemeral</button>
              </div>
              {activeView === "review" ? (
                <button className="secondary-button" disabled={!selectedIds.length || Boolean(busy)} onClick={() => approve(selectedIds)} type="button">
                  <CheckCheck size={16} /> Approve selected {selectedIds.length ? `(${selectedIds.length})` : ""}
                </button>
              ) : null}
            </section>

            <div className="content-layout">
              <section className="queue-panel">
                <div className="panel-heading">
                  <div><h2>{viewTitle(activeView)}</h2><p>{visiblePosts.length} visible</p></div>
                  <Filter size={16} />
                </div>
                <div className="post-list">
                  {visiblePosts.length ? visiblePosts.map((post) => (
                    <PostRow
                      key={post.id}
                      post={post}
                      active={activePost?.id === post.id}
                      selected={selectedIds.includes(post.id)}
                      now={state.server_time}
                      selectionEnabled={activeView === "review"}
                      onSelect={() => setActivePostId(post.id)}
                      onToggle={() => setSelectedIds((current) => current.includes(post.id) ? current.filter((id) => id !== post.id) : [...current, post.id])}
                    />
                  )) : <EmptyQueue view={activeView} />}
                </div>
              </section>

              <section className="review-panel">
                {activePost ? (
                  <PostReview
                    key={activePost.id}
                    post={activePost}
                    now={state.server_time}
                    openedActionId={openedActionId}
                    busy={Boolean(busy)}
                    onApprove={() => approve([activePost.id])}
                    onReject={(note) => reject(activePost, note)}
                    onSave={save}
                    onPublish={(action) => publishAction(activePost, action)}
                    onMarkPublished={(action) => markPublished(activePost, action)}
                  />
                ) : <div className="empty-review"><Megaphone size={28} /><h2>No communication selected</h2></div>}
              </section>
            </div>
          </>
        )}
      </main>

      {createOpen ? <CreatePostModal busy={Boolean(busy)} onClose={() => setCreateOpen(false)} onCreate={createPost} /> : null}
    </div>
  );
}

function NavButton({ icon, label, count, selected, onClick }: { icon: ReactNode; label: string; count?: number; selected: boolean; onClick: () => void }) {
  return <button className={selected ? "selected" : ""} onClick={onClick} type="button">{icon}<span>{label}</span>{typeof count === "number" ? <strong>{count}</strong> : null}</button>;
}

function viewTitle(view: View): string {
  return { review: "Needs approval", approved: "Ready to publish", published: "Published", archive: "Expired ephemeral", rotation: "Channel rotation", social: "Social growth", activity: "Activity log" }[view];
}

function Metric({ icon, label, value, tone }: { icon: ReactNode; label: string; value: number; tone: string }) {
  return <div className={`metric ${tone}`}><span>{icon}</span><div><strong>{value}</strong><p>{label}</p></div></div>;
}

function Banner({ icon, text, tone }: { icon: ReactNode; text: string; tone: string }) {
  return <div className={`banner ${tone}`}>{icon}<span>{text}</span></div>;
}

function PostRow({ post, active, selected, selectionEnabled, now, onSelect, onToggle }: { post: CommunicationPost; active: boolean; selected: boolean; selectionEnabled: boolean; now: number; onSelect: () => void; onToggle: () => void }) {
  const thumbnail = post.assets.find((asset) => asset.role === "thumbnail" || asset.role === "persistent_square" || asset.role === "ephemeral_square") || post.assets[0];
  return (
    <article className={`post-row ${active ? "active" : ""}`}>
      {selectionEnabled ? <button className={`check-button ${selected ? "checked" : ""}`} onClick={onToggle} aria-label={`Select ${post.title}`} type="button">{selected ? <Check size={14} /> : null}</button> : null}
      <button className="post-row-main" onClick={onSelect} type="button">
        <span className="thumbnail">{thumbnail ? <img src={thumbnail.url} alt="" /> : <ImageIcon size={18} />}</span>
        <span className="row-copy">
          <span className="row-top"><strong>{post.title}</strong><StatusPill status={post.status} /></span>
          <span className="row-meta">{post.game_slug || "Studio"} · {post.channels.length} channels</span>
          <span className={`lifecycle ${post.content_type}`}>{post.content_type === "persistent" ? "Persistent" : remainingLabel(post.expires_at, now)}</span>
        </span>
      </button>
    </article>
  );
}

function StatusPill({ status, label }: { status: string; label?: string }) {
  return <span className={`status-pill ${status}`}>{label || status}</span>;
}

function PostReview({ post, now, openedActionId, busy, onApprove, onReject, onSave, onPublish, onMarkPublished }: {
  post: CommunicationPost;
  now: number;
  openedActionId: string;
  busy: boolean;
  onApprove: () => void;
  onReject: (note: string) => void;
  onSave: (post: CommunicationPost) => void;
  onPublish: (action: ChannelAction) => void;
  onMarkPublished: (action: ChannelAction) => void;
}) {
  const [draft, setDraft] = useState(post);
  const [activeLocale, setActiveLocale] = useState(Object.keys(post.locales)[0] || "en");
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const changed = JSON.stringify(draft) !== JSON.stringify(post);
  const isReviewable = ["draft", "pending", "rejected"].includes(post.status);

  useEffect(() => {
    setDraft(post);
    setActiveLocale(Object.keys(post.locales)[0] || "en");
  }, [post]);

  const updateLocale = (copy: string) => setDraft((current) => ({ ...current, locales: { ...current.locales, [activeLocale]: copy } }));

  return (
    <div className="review-content">
      <div className="review-header">
        <div>
          <div className="badge-line"><span className={`type-badge ${post.content_type}`}>{post.content_type}</span><StatusPill status={post.status} /><span className="priority">P{post.priority}</span></div>
          <input className="title-input" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} aria-label="Post title" />
          <p>{post.campaign || "Unassigned campaign"} · {post.audience}</p>
        </div>
        <div className="review-actions">
          {changed ? <button className="secondary-button" onClick={() => onSave(draft)} disabled={busy} type="button"><Pencil size={15} /> Save</button> : null}
          {isReviewable ? <button className="primary-button" onClick={onApprove} disabled={busy} type="button"><Check size={16} /> Approve</button> : null}
        </div>
      </div>

      <div className="metadata-strip">
        <span><CalendarClock size={15} /> {post.publish_after ? formatDate(post.publish_after) : "Publish when approved"}</span>
        <span><TimerReset size={15} /> {post.content_type === "ephemeral" ? `${remainingLabel(post.expires_at, now)} · ${formatDate(post.expires_at)}` : "No expiry"}</span>
        <span><Globe2 size={15} /> {Object.keys(post.locales).length} languages</span>
      </div>

      <section className="review-section">
        <div className="section-title"><div><p className="eyebrow">Copy</p><h3>Localized post</h3></div><Languages size={18} /></div>
        <div className="locale-tabs">
          {Object.keys(post.locales).map((locale) => <button className={activeLocale === locale ? "selected" : ""} onClick={() => setActiveLocale(locale)} key={locale} type="button">{locale.toUpperCase()}</button>)}
        </div>
        <textarea className="copy-editor" value={draft.locales[activeLocale] || ""} onChange={(event) => updateLocale(event.target.value)} aria-label={`${localeLabels[activeLocale] || activeLocale} copy`} />
      </section>

      <section className="review-section">
        <div className="section-title"><div><p className="eyebrow">Visual system</p><h3>Images and trailer variants</h3></div><ImageIcon size={18} /></div>
        {post.assets.length ? <div className="asset-grid">{post.assets.map((asset, index) => (
          <figure className={`asset-item ${asset.role.startsWith("ephemeral") ? "ephemeral" : "persistent"} ${asset.safety || "sfw"}`} key={`${asset.role}-${index}`}>
            <img src={asset.url} alt={asset.alt || ""} />
            <figcaption>
              <strong>{assetRoleLabels[asset.role] || asset.role}</strong>
              <span>{[asset.safety?.toUpperCase(), asset.duration_seconds ? `${asset.duration_seconds}s` : "", asset.format || "Visual"].filter(Boolean).join(" · ")}</span>
            </figcaption>
            {asset.source_path ? <div className="asset-source" title={asset.source_path}>{asset.source_path}</div> : null}
          </figure>
        ))}</div> : <div className="missing-assets"><ImageIcon size={20} /><span>No visuals attached yet.</span></div>}
      </section>

      <section className="review-section channel-section">
        <div className="section-title"><div><p className="eyebrow">Distribution</p><h3>{post.channels.length} publishing actions</h3></div><Send size={18} /></div>
        <div className="channel-list">
          {post.channels.map((action) => (
            <div className="channel-row" key={action.id}>
              <span className="channel-icon">{action.mode === "api" ? <Rocket size={16} /> : action.mode === "link" ? <Link2 size={16} /> : <Clipboard size={16} />}</span>
              <div><strong>{action.label}</strong><p>{action.mode.toUpperCase()} · {action.locale.toUpperCase()} · {assetRoleLabels[action.asset_role] || action.asset_role || "No visual"}</p>{action.last_error ? <small>{action.last_error}</small> : null}</div>
              <StatusPill status={action.status} label={action.channel === "patreon" && action.status === "ready" ? "drafted" : undefined} />
              {action.channel === "patreon" && action.status === "ready" ? <>
                {action.external_url ? <a className="icon-link" href={action.external_url} target="_blank" rel="noreferrer" aria-label="Open Patreon draft"><ExternalLink size={16} /></a> : null}
                <button className="done-button" onClick={() => onMarkPublished(action)} disabled={busy} type="button"><Check size={15} /> Published</button>
              </> : action.status === "published" ? action.external_url ? <a className="icon-link" href={action.external_url} target="_blank" rel="noreferrer" aria-label={`Open ${action.label}`}><ExternalLink size={16} /></a> : null : openedActionId === action.id ? (
                <button className="done-button" onClick={() => onMarkPublished(action)} disabled={busy} type="button"><Check size={15} /> Done</button>
              ) : (
                <button className="publish-button" onClick={() => onPublish(action)} disabled={busy || post.status !== "approved"} type="button">{action.mode === "api" ? "Publish" : "Copy + open"}</button>
              )}
            </div>
          ))}
        </div>
      </section>

      {isReviewable ? <div className="reject-zone">
        {rejecting ? <><input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Short correction note" /><button onClick={() => onReject(note)} disabled={busy} type="button">Send back</button><button className="icon-button" onClick={() => setRejecting(false)} type="button" aria-label="Cancel"><X size={16} /></button></> : <button className="text-button danger" onClick={() => setRejecting(true)} type="button"><Trash2 size={15} /> Request changes</button>}
      </div> : null}
    </div>
  );
}

function ActivityView({ audit, posts }: { audit: AuditEntry[]; posts: CommunicationPost[] }) {
  const byId = new Map(posts.map((post) => [post.id, post.title]));
  return <section className="activity-panel"><div className="panel-heading"><div><h2>Recent activity</h2><p>{audit.length} latest actions</p></div><History size={18} /></div><div className="activity-list">{audit.map((entry) => <div className="activity-row" key={entry.id}><span><History size={15} /></span><div><strong>{entry.action.replaceAll(".", " ")}</strong><p>{entry.post_id ? byId.get(entry.post_id) || "Removed post" : "Approval desk"}</p></div><time>{formatDate(entry.created_at)}</time></div>)}</div></section>;
}

function RotationView({ platforms, now }: { platforms: PlatformHealth[]; now: number }) {
  const healthy = platforms.filter((platform) => platform.state === "healthy").length;
  const due = platforms.filter((platform) => platform.state === "due").length;
  const blocked = platforms.filter((platform) => platform.state === "blocked").length;
  return (
    <div className="rotation-view">
      <section className="rotation-summary" aria-label="Rotation summary">
        <Metric icon={<CircleCheckBig size={18} />} label="On rhythm" value={healthy} tone="green" />
        <Metric icon={<CalendarClock size={18} />} label="Due now" value={due} tone="amber" />
        <Metric icon={<CircleX size={18} />} label="Blocked" value={blocked} tone="coral" />
        <Metric icon={<Send size={18} />} label="Queued actions" value={platforms.reduce((sum, platform) => sum + platform.queued, 0)} tone="blue" />
      </section>
      <section className="rotation-panel">
        <div className="panel-heading"><div><h2>Publishing coverage</h2><p>Every core channel has a visible rhythm and execution path.</p></div><RadioTower size={18} /></div>
        <div className="rotation-list">
          {platforms.map((platform) => (
            <article className={`rotation-row ${platform.state}`} key={platform.id}>
              <div className="rotation-identity"><strong>{platform.label}</strong><span>{platform.format}</span></div>
              <div><span>Target</span><strong>Every {platform.target_hours < 84 ? `${Math.round(platform.target_hours / 24)} days` : `${Math.round(platform.target_hours / 24 / 7)} week`}</strong></div>
              <div><span>Last published</span><strong>{platform.last_published_at ? formatDate(platform.last_published_at) : "No verified post"}</strong></div>
              <div><span>{platform.overdue ? "Overdue" : "Next due"}</span><strong>{platform.overdue ? `${Math.max(1, Math.floor((now - platform.next_due_at) / 3600))}h` : formatDate(platform.next_due_at)}</strong></div>
              <div className="rotation-execution"><StatusPill status={platform.state} /><small>{platform.publisher === "api" ? "Automatic API" : platform.publisher === "blocked" ? "API setup needed" : "Ready for owner click"}{platform.queued ? ` · ${platform.queued} queued` : ""}</small></div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function SocialGrowthView({ networks, actions, activeNetworkId, filter, busy, onSelectNetwork, onFilter, onSave, onApprove, onDecline, onComplete }: {
  networks: SocialNetwork[];
  actions: SocialAction[];
  activeNetworkId: string;
  filter: "review" | "approved" | "declined" | "all";
  busy: boolean;
  onSelectNetwork: (id: string) => void;
  onFilter: (filter: "review" | "approved" | "declined" | "all") => void;
  onSave: (action: SocialAction) => void;
  onApprove: (action: SocialAction) => void;
  onDecline: (action: SocialAction) => void;
  onComplete: (action: SocialAction) => void;
}) {
  const network = networks.find((item) => item.id === activeNetworkId) || networks[0];
  const networkActions = actions.filter((action) => {
    if (!network || action.network_id !== network.id) return false;
    if (filter === "review") return action.status === "proposed";
    if (filter === "approved") return action.status === "approved" || action.status === "completed";
    if (filter === "declined") return action.status === "declined";
    return true;
  });

  if (!network) return <section className="activity-panel"><div className="empty-review"><Users size={28} /><h2>No social networks configured</h2></div></section>;

  return (
    <div className="social-growth">
      <div className="social-safety-banner">
        <ShieldCheck size={19} />
        <div><strong>You keep the final word</strong><p>New actions arrive daily. Approval authorises the exact target and wording shown here; the executor checks the approved queue once per hour.</p></div>
      </div>

      <div className="network-tabs" role="tablist" aria-label="Social networks">
        {networks.map((item) => {
          const proposed = actions.filter((action) => action.network_id === item.id && action.status === "proposed").length;
          return <button className={item.id === network.id ? "selected" : ""} onClick={() => onSelectNetwork(item.id)} role="tab" aria-selected={item.id === network.id} key={item.id} type="button"><span>{item.label}</span>{proposed ? <strong>{proposed}</strong> : null}</button>;
        })}
      </div>

      <section className="network-brief">
        <div className="network-identity">
          <span className={`network-tier ${network.tier}`}>{network.tier}</span>
          <div><p className="eyebrow">{network.handle}</p><h2>{network.label}</h2></div>
          {network.profile_url ? <a className="icon-link" href={network.profile_url} target="_blank" rel="noreferrer" aria-label={`Open ${network.label}`}><ExternalLink size={16} /></a> : null}
        </div>
        <div className="network-plan">
          <div><span>Growth focus</span><strong>{network.growth_focus}</strong></div>
          <div><span>People to reach</span><strong>{network.audience}</strong></div>
          <div><span>Rhythm</span><strong>{network.cadence}</strong></div>
          <div><span>Guardrail</span><strong>{network.constraints}</strong></div>
        </div>
      </section>

      <div className="social-toolbar">
        <div><p className="eyebrow">Proposed actions</p><strong>{networkActions.length} visible for {network.label}</strong></div>
        <div className="segmented" aria-label="Social proposal status">
          <button className={filter === "review" ? "selected" : ""} onClick={() => onFilter("review")} type="button">To review</button>
          <button className={filter === "approved" ? "selected" : ""} onClick={() => onFilter("approved")} type="button">Approved</button>
          <button className={filter === "declined" ? "selected" : ""} onClick={() => onFilter("declined")} type="button">Declined</button>
          <button className={filter === "all" ? "selected" : ""} onClick={() => onFilter("all")} type="button">All</button>
        </div>
      </div>

      <div className="social-action-list">
        {networkActions.length ? networkActions.map((action) => (
          <SocialActionCard key={action.id} action={action} busy={busy} onSave={onSave} onApprove={onApprove} onDecline={onDecline} onComplete={onComplete} />
        )) : <div className="social-empty"><CircleCheckBig size={25} /><strong>No actions in this view</strong><p>Switch status or choose another network.</p></div>}
      </div>
    </div>
  );
}

function SocialActionCard({ action, busy, onSave, onApprove, onDecline, onComplete }: {
  action: SocialAction;
  busy: boolean;
  onSave: (action: SocialAction) => void;
  onApprove: (action: SocialAction) => void;
  onDecline: (action: SocialAction) => void;
  onComplete: (action: SocialAction) => void;
}) {
  const [draft, setDraft] = useState(action);
  useEffect(() => setDraft(action), [action]);
  const changed = JSON.stringify(draft) !== JSON.stringify(action);
  const canReview = action.status === "proposed";
  return (
    <article className={`social-action-card ${action.status}`}>
      <div className="social-action-icon">{socialActionIcon(action.kind)}</div>
      <div className="social-action-main">
        <div className="social-action-head">
          <div className="social-action-labels"><span className="action-kind">{action.kind.replaceAll("_", " ")}</span><StatusPill status={action.status} /><span className="priority">P{action.priority}</span></div>
          <div className="social-action-buttons">
            {changed ? <button className="secondary-button" onClick={() => onSave(draft)} disabled={busy} type="button"><Pencil size={15} /> Save</button> : null}
            {canReview ? <button className="secondary-button danger-outline" onClick={() => onDecline(action)} disabled={busy} type="button"><CircleX size={15} /> Decline</button> : null}
            {canReview ? <button className="primary-button" onClick={() => onApprove(changed ? draft : action)} disabled={busy || changed} title={changed ? "Save edits before approval" : "Approve this exact action for the hourly executor"} type="button"><BadgeCheck size={16} /> Approve</button> : null}
            {action.status === "approved" && action.execution_state === "blocked" ? <button className="done-button" onClick={() => onSave(draft)} disabled={busy || changed} title={changed ? "Save the edited action first" : "Queue this action for another hourly attempt"} type="button"><TimerReset size={15} /> Retry</button> : null}
            {action.status === "approved" && action.execution_state !== "blocked" ? <button className="done-button" onClick={() => onComplete(action)} disabled={busy} type="button"><Check size={15} /> Mark done</button> : null}
          </div>
        </div>
        <input className="social-title-input" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} aria-label="Proposal title" />
        {action.target_name || action.target_url ? <div className="social-target"><div><span>Target</span><strong>{draft.target_name || "Open source"}</strong></div>{action.target_url ? <a className="secondary-button" href={action.target_url} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Open</a> : null}</div> : null}
        {action.target_excerpt ? <blockquote>{action.target_excerpt}</blockquote> : null}
        {action.draft_text || ["reply", "comment", "community_care"].includes(action.kind) ? <label className="social-draft"><span>Prepared wording</span><textarea value={draft.draft_text} onChange={(event) => setDraft({ ...draft, draft_text: event.target.value })} placeholder="No wording needed for this action." /></label> : null}
        <div className="social-action-notes">
          <div><span>Why it helps</span><p>{action.rationale}</p></div>
          <div><span>Before doing it</span><p>{action.guardrails}</p></div>
        </div>
        {action.execution_state === "blocked" && action.execution_error ? <div className="social-execution-error"><strong>Needs attention</strong><p>{action.execution_error}</p></div> : null}
        <footer><span><AtSign size={13} /> {action.source}</span><span><Lock size={13} /> {action.status === "approved" ? action.execution_state === "blocked" ? "Blocked after execution attempt" : "Queued for hourly execution" : action.status === "completed" ? "Execution recorded" : "Nothing happens before approval"}</span></footer>
      </div>
    </article>
  );
}

function socialActionIcon(kind: SocialAction["kind"]): ReactNode {
  if (kind === "follow") return <UserPlus size={19} />;
  if (kind === "like") return <Heart size={19} />;
  if (kind === "upvote" || kind === "boost") return <ThumbsUp size={19} />;
  if (kind === "reply" || kind === "comment") return <MessageCircle size={19} />;
  if (kind === "community_care" || kind === "react") return <Users size={19} />;
  if (kind === "profile") return <BadgeCheck size={19} />;
  return <Search size={19} />;
}

function EmptyQueue({ view }: { view: View }) {
  return <div className="empty-queue"><Sparkles size={26} /><strong>{view === "review" ? "Everything is reviewed" : "Nothing here yet"}</strong><p>{view === "review" ? "The next prepared campaign will appear here." : "This view will fill as communication moves forward."}</p></div>;
}

function CreatePostModal({ busy, onClose, onCreate }: { busy: boolean; onClose: () => void; onCreate: (payload: unknown) => void }) {
  const [type, setType] = useState<ContentType>("persistent");
  const [selectedChannels, setSelectedChannels] = useState<string[]>(defaultChannelIds);
  const [assets, setAssets] = useState({
    persistent_hero: "",
    persistent_square: "",
    ephemeral_story: "",
    ephemeral_reel: "",
    trailer_sfw_short: "",
    trailer_sfw_short_vertical: "",
    trailer_sfw_long: "",
    trailer_nsfw_short: "",
    trailer_nsfw_long: "",
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const english = String(data.get("english") || "");
    const payload = {
      title: data.get("title"),
      summary: data.get("summary"),
      game_slug: data.get("game_slug"),
      campaign: data.get("campaign"),
      audience: data.get("audience"),
      priority: Number(data.get("priority") || 50),
      content_type: type,
      expires_at: type === "ephemeral" ? data.get("expires_at") : null,
      publish_after: data.get("publish_after") || null,
      locales: { en: english },
      assets: Object.entries(assets).filter(([, url]) => url).map(([role, url]) => ({ role, url })),
      channels: channelDirectory.filter((channel) => selectedChannels.includes(channel.id)).map((channel) => ({
        channel: channel.id,
        label: channel.label,
        mode: channel.mode,
        adapter: "adapter" in channel ? channel.adapter : "",
        target_url: channel.url,
        locale: "en",
        asset_role: type === "ephemeral" ? "ephemeral_story" : "persistent_hero",
        copy_text: english,
      })),
      source: "approval-hub",
    };
    onCreate(payload);
  };

  return <div className="modal-backdrop" role="presentation"><form className="modal" onSubmit={submit}>
    <div className="modal-header"><div><p className="eyebrow">New communication</p><h2>Prepare approval package</h2></div><button className="icon-button" onClick={onClose} type="button" aria-label="Close"><X size={18} /></button></div>
    <div className="form-grid">
      <label className="wide">Title<input name="title" required maxLength={180} /></label>
      <label>Game<input name="game_slug" placeholder="vorenkas-lewd-tales" /></label>
      <label>Campaign<input name="campaign" placeholder="October world update" /></label>
      <label>Audience<select name="audience" defaultValue="all"><option value="all">All audiences</option><option value="active-free-patreon">Active free Patreon</option><option value="active-paid-patreon">Active paying Patreon</option><option value="acquisition-realistic">Realistic acquisition</option><option value="vorenka-core">Vorenka core</option></select></label>
      <label>Priority<input name="priority" type="number" min="0" max="100" defaultValue="70" /></label>
      <label>Publish after<input name="publish_after" type="datetime-local" /></label>
      <label>Expiry<input name="expires_at" type="datetime-local" required={type === "ephemeral"} disabled={type === "persistent"} /></label>
      <fieldset className="wide type-choice"><legend>Lifecycle</legend><button className={type === "persistent" ? "selected" : ""} onClick={() => setType("persistent")} type="button"><FileText size={17} /><span><strong>Persistent</strong><small>News, release, guide, evergreen</small></span></button><button className={type === "ephemeral" ? "selected" : ""} onClick={() => setType("ephemeral")} type="button"><TimerReset size={17} /><span><strong>Ephemeral</strong><small>Event, countdown, temporary push</small></span></button></fieldset>
      <label className="wide">Short summary<textarea name="summary" rows={2} /></label>
      <label className="wide">English copy<textarea name="english" rows={7} required /></label>
    </div>
    <div className="asset-inputs"><p className="eyebrow">Distinct visual assets</p>{Object.keys(assets).map((role) => <label key={role}><span>{assetRoleLabels[role]}</span><input value={assets[role as keyof typeof assets]} onChange={(event) => setAssets({ ...assets, [role]: event.target.value })} type="url" placeholder="https://..." /></label>)}</div>
    <div className="channel-picker"><p className="eyebrow">Distribution coverage</p><div>{channelDirectory.map((channel) => <label key={channel.id}><input type="checkbox" checked={selectedChannels.includes(channel.id)} onChange={() => setSelectedChannels((current) => current.includes(channel.id) ? current.filter((id) => id !== channel.id) : [...current, channel.id])} /><span>{channel.label}</span></label>)}</div></div>
    <div className="modal-actions"><button className="secondary-button" onClick={onClose} type="button">Cancel</button><button className="primary-button" disabled={busy} type="submit"><Plus size={16} /> Add to review</button></div>
  </form></div>;
}

function LoginScreen({ error, loading, onLogin }: { error: string; loading: boolean; onLogin: (password: string) => void }) {
  const [password, setPassword] = useState("");
  return <main className="login-screen"><form className="login-panel" onSubmit={(event) => { event.preventDefault(); onLogin(password); }}><span className="login-mark"><Lock size={22} /></span><div><p className="eyebrow">Private operations</p><h1>Shaste Comms</h1><p>Communication approval desk</p></div><label>Password<input autoFocus type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>{error ? <div className="login-error">{error}</div> : null}<button className="primary-button" disabled={loading} type="submit">{loading ? <LoaderCircle className="spin" size={16} /> : <ShieldCheck size={16} />} Unlock</button></form></main>;
}

function BootScreen() {
  return <div className="boot-screen"><LoaderCircle className="spin" size={28} /></div>;
}

createRoot(document.getElementById("root")!).render(<StrictMode><App /></StrictMode>);
